import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { PLATFORM_COLLECTIONS } from './platformSchema';
import { loadRecordsByEquality } from './firestoreQueryHelpers';
import { requireOrgWriteActor } from './operationalWriteGate';
import { MANUAL_BOOKING_SOURCE } from './bookingSource';
import { isSourceBackedCommercial, SOURCE_OWNED_EDIT_MESSAGE } from './sourceAuthority';
import { assertExplicitAreaForNewRecord } from './operatingAreas';
import { resolveOrgTimezone } from './organizationRuntime';
import {
  notifyBookingFollowUpRequested,
  notifyBookingChangeApproved,
  notifyBookingChangeDeclined,
  notifyBookingChangeRequested,
  notifyBookingNoShow,
  notifyBookingRequestAccepted,
  notifyBookingRequestCreated,
  notifyBookingRequestDeclined,
  notifyBookingRequestCanceled,
  notifyBookingRequestCompleted,
  notifyBookingConfirmed,
  notifyBookingRescheduled,
  notifyBookingCanceled,
  notifyBookingCompleted,
  notifyFeedbackReadyForBooking,
  notifyNextSessionRecommended,
  notifyPostSessionUploadPrompt,
  notifyUpcomingBookingReminder,
  notifyBookingPaymentPending,
} from './notifications';
import { generateAiReviewDraft } from './aiReviewApi';
import {
  recordAndNotifyBookingEdit,
  recordAndNotifyStatusChange,
  recordAndNotifyReassignment,
  SCHEDULE_CHANGE_TYPES,
} from './scheduleChanges';
import { assessOperationalReadiness } from './scheduleModel';
// SnoPro Accelerator F — the booking read-normalisers now live in the PURE `bookingDomain` module (no
// Firestore/React/notifications) so the partner-integration domain graph is server-importable. booking.js
// re-exports them below so every `import { normalizeBooking, ... } from './booking'` consumer is unchanged.
import {
  normalizeString,
  normalizeEmail,
  normalizeBookingRequest,
  normalizeBookingChangeRequest,
  normalizeBookingCalendarState,
  normalizeBookingCommercialState,
  normalizeBooking,
} from './bookingDomain';

export {
  normalizeBookingRequest,
  normalizeBookingChangeRequest,
  normalizeBookingCalendarState,
  normalizeBookingCommercialState,
  normalizeBooking,
};

// Pass 49A §3: the canonical organizationMember document id is deterministic —
// `${userId}::${organizationId}` (see organizationData/importInstructors/seedTestUsers).
export function memberDocId(userId = '', organizationId = '') {
  return `${userId}::${organizationId}`;
}

// Pass 55 Stage F: the canonical document id for a sheet-sourced booking is DETERMINISTIC on its
// permanent cross-system identity (externalId = Motus ID) + organization — mirroring the member id
// shape. This makes the rebaseline cutover idempotent: the same Motus ID always maps to the same
// document, so a retried atomic write can never create a duplicate. externalId semantics are unchanged.
export function sheetBookingDocId(externalId = '', organizationId = '') {
  return `${externalId}::${organizationId}`;
}

// Pass 49A §3 (review): resolve a canonical member's authoritative linked Firebase Auth
// UID, SCOPED to the organisation. NEVER scan userId globally + filter client-side (that
// is not a safe multi-tenant query — security rules are not post-query filters).
//   1. Deterministic id lookup: organizationMembers/`${userId}::${organizationId}`,
//      verified on BOTH organizationId AND userId.
//   2. Fallback for legacy auto-id members: a single query constrained by BOTH
//      organizationId AND userId (never userId alone).
// Returns the member's authUserId, or '' when the member has no linked Auth account.
export async function resolveMemberAuthUserId(db, organizationId = '', userId = '') {
  if (!db || !organizationId || !userId) return '';
  try {
    const detId = memberDocId(userId, organizationId);
    const detSnap = await getDoc(doc(db, PLATFORM_COLLECTIONS.ORGANIZATION_MEMBERS, detId));
    if (detSnap && detSnap.exists()) {
      const d = detSnap.data() || {};
      if (d.organizationId === organizationId && d.userId === userId) {
        return d.authUserId || '';
      }
    }
    // Legacy fallback: members created via addDoc have a non-deterministic id. Query is
    // constrained by BOTH fields so it can never surface another tenant's record.
    const snap = await getDocs(query(
      collection(db, PLATFORM_COLLECTIONS.ORGANIZATION_MEMBERS),
      where('organizationId', '==', organizationId),
      where('userId', '==', userId),
    ));
    const m = snap.docs.map((x) => x.data()).find((x) => x.authUserId);
    return m ? m.authUserId : '';
  } catch {
    return '';
  }
}

// Pass 49A §review3: remove ONE review-reason token from a `;`-separated reason string,
// preserving every unrelated reason. Case-insensitive substring match. Smallest safe
// helper — no review framework.
export function removeReviewReasonToken(reasonString = '', token = '') {
  const t = String(token || '').toLowerCase();
  if (!t) return String(reasonString || '');
  return String(reasonString || '')
    .split(';')
    .map((s) => s.trim())
    .filter(Boolean)
    .filter((s) => !s.toLowerCase().includes(t))
    .join('; ');
}

function toDateValue(value) {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (value?.seconds) {
    return new Date(value.seconds * 1000);
  }
  if (value instanceof Date) return value;
  return null;
}

function toTimeNumber(value = '') {
  const [hours = '0', minutes = '0'] = String(value || '').split(':');
  return (Number(hours) * 60) + Number(minutes);
}

function rangesOverlap(startA, endA, startB, endB) {
  const aStart = toDateValue(startA)?.getTime?.();
  const aEnd = toDateValue(endA)?.getTime?.();
  const bStart = toDateValue(startB)?.getTime?.();
  const bEnd = toDateValue(endB)?.getTime?.();

  if ([aStart, aEnd, bStart, bEnd].some((value) => typeof value !== 'number' || Number.isNaN(value))) {
    return false;
  }

  return aStart < bEnd && bStart < aEnd;
}

function slotRangesOverlap(slotA = {}, slotB = {}) {
  const sameSpecificDate = Boolean(
    slotA.specificDate &&
    slotB.specificDate &&
    slotA.specificDate === slotB.specificDate
  );
  const sameRecurringDay = Boolean(
    slotA.dayOfWeek &&
    slotB.dayOfWeek &&
    slotA.dayOfWeek === slotB.dayOfWeek
  );

  if (!sameSpecificDate && !sameRecurringDay) {
    return false;
  }

  const aStart = toTimeNumber(slotA.startTime);
  const aEnd = toTimeNumber(slotA.endTime);
  const bStart = toTimeNumber(slotB.startTime);
  const bEnd = toTimeNumber(slotB.endTime);

  return aStart < bEnd && bStart < aEnd;
}

function bookingWindowFitsAvailability(bookingLike = {}, availabilitySlot = {}) {
  const startDate = toDateValue(bookingLike.startAt || bookingLike.requestedStartAt);
  const endDate = toDateValue(bookingLike.endAt || bookingLike.requestedEndAt);

  if (!startDate || !endDate) return false;

  if (availabilitySlot.specificDate) {
    const dateKey = startDate.toISOString().slice(0, 10);
    if (availabilitySlot.specificDate !== dateKey) return false;
  } else if (availabilitySlot.dayOfWeek) {
    const weekday = startDate.toLocaleDateString('en-US', { weekday: 'long', timeZone: bookingLike.timezone || 'UTC' });
    if (availabilitySlot.dayOfWeek.toLowerCase() !== weekday.toLowerCase()) return false;
  }

  const slotStart = toTimeNumber(availabilitySlot.startTime);
  const slotEnd = toTimeNumber(availabilitySlot.endTime);
  const requestStart = startDate.getHours() * 60 + startDate.getMinutes();
  const requestEnd = endDate.getHours() * 60 + endDate.getMinutes();

  return requestStart >= slotStart && requestEnd <= slotEnd;
}

export function normalizeAvailabilitySlot(record = {}, fallbackId = '') {
  const id = record.id || record.slotId || fallbackId || '';

  return {
    id,
    slotId: id,
    organizationId: record.organizationId || '',
    instructorId: record.instructorId || '',
    timezone: normalizeString(record.timezone || 'UTC'),
    dayOfWeek: normalizeString(record.dayOfWeek || ''),
    specificDate: normalizeString(record.specificDate || ''),
    startTime: normalizeString(record.startTime || ''),
    endTime: normalizeString(record.endTime || ''),
    isRecurring: Boolean(record.isRecurring),
    status: normalizeString(record.status || 'active'),
    visibility: normalizeString(record.visibility || 'private'),
    externalCalendarRef: normalizeString(record.externalCalendarRef || ''),
    createdAt: record.createdAt || null,
    updatedAt: record.updatedAt || null,
  };
}

export function getConflictingAvailabilitySlots(slots = [], candidateSlot = {}, { excludeSlotId = '' } = {}) {
  const normalizedCandidate = normalizeAvailabilitySlot(candidateSlot, candidateSlot.slotId || candidateSlot.id || '');
  return (slots || [])
    .map((slot) => normalizeAvailabilitySlot(slot))
    .filter((slot) => slot.status === 'active')
    .filter((slot) => slot.slotId !== excludeSlotId && slot.slotId !== normalizedCandidate.slotId)
    .filter((slot) => slot.instructorId === normalizedCandidate.instructorId)
    .filter((slot) => slotRangesOverlap(slot, normalizedCandidate));
}

export async function hasAvailabilityConflict(db, candidateSlot = {}, options = {}) {
  const slots = await getInstructorAvailability(db, candidateSlot.instructorId || '');
  return getConflictingAvailabilitySlots(slots, candidateSlot, options).length > 0;
}

export async function createAvailabilitySlot(
  db,
  {
    organizationId = '',
    instructorId = '',
    timezone = 'UTC',
    dayOfWeek = '',
    specificDate = '',
    startTime = '',
    endTime = '',
    isRecurring = false,
    status = 'active',
    visibility = 'private',
    externalCalendarRef = '',
  } = {}
) {
  const payload = normalizeAvailabilitySlot({
    organizationId,
    instructorId,
    timezone,
    dayOfWeek,
    specificDate,
    startTime,
    endTime,
    isRecurring,
    status,
    visibility,
    externalCalendarRef,
  });

  if (payload.instructorId && await hasAvailabilityConflict(db, payload)) {
    throw new Error('Availability slot conflicts with an existing slot.');
  }

  const docRef = (await addDoc(collection(db, PLATFORM_COLLECTIONS.AVAILABILITY_SLOTS), {
    ...payload,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })) || {};

  return normalizeAvailabilitySlot(payload, docRef.id || '');
}

export async function updateAvailabilitySlot(db, slotId = '', updates = {}) {
  if (!slotId) throw new Error('Availability slot id is required.');

  const payload = normalizeAvailabilitySlot(updates, slotId);
  if (payload.instructorId && await hasAvailabilityConflict(db, payload, { excludeSlotId: slotId })) {
    throw new Error('Availability slot conflicts with an existing slot.');
  }
  await updateDoc(doc(db, PLATFORM_COLLECTIONS.AVAILABILITY_SLOTS, slotId), {
    ...payload,
    updatedAt: serverTimestamp(),
  });

  return payload;
}

export async function getInstructorAvailability(db, instructorId = '') {
  if (!instructorId) return [];
  const records = await loadRecordsByEquality(db, {
    collectionName: PLATFORM_COLLECTIONS.AVAILABILITY_SLOTS,
    field: 'instructorId',
    value: instructorId,
  });
  return records.map((record) => normalizeAvailabilitySlot(record));
}

export async function getOrganizationAvailability(db, organizationId = '') {
  if (!organizationId) return [];
  const records = await loadRecordsByEquality(db, {
    collectionName: PLATFORM_COLLECTIONS.AVAILABILITY_SLOTS,
    field: 'organizationId',
    value: organizationId,
  });
  return records.map((record) => normalizeAvailabilitySlot(record));
}

async function queryPublicAvailability(db, filters = []) {
  try {
    const constraints = [
      ...filters.map((filter) => where(filter.field, filter.operator || '==', filter.value)),
      where('status', '==', 'active'),
      where('visibility', '==', 'public'),
      orderBy('specificDate', 'asc'),
    ];
    const snapshot = await getDocs(query(collection(db, PLATFORM_COLLECTIONS.AVAILABILITY_SLOTS), ...constraints));
    return snapshot.docs.map((docSnap) => normalizeAvailabilitySlot({ id: docSnap.id, ...docSnap.data() }));
  } catch (error) {
    console.warn('Public availability query failed:', error);
    return [];
  }
}

export async function getPublicInstructorAvailability(db, instructorId = '') {
  if (!instructorId) return [];
  return queryPublicAvailability(db, [{ field: 'instructorId', value: instructorId }]);
}

export async function getPublicOrganizationAvailability(db, organizationId = '') {
  if (!organizationId) return [];
  return queryPublicAvailability(db, [{ field: 'organizationId', value: organizationId }]);
}

export function getBookingCommercialState(booking = {}) {
  return normalizeBookingCommercialState(booking);
}

export function isBookingCoveredByPlanOrPackage(booking = {}) {
  const commercialState = getBookingCommercialState(booking);
  return ['subscription', 'plan', 'package', 'organization_subscription'].includes(commercialState.billingSourceType)
    || Boolean(commercialState.packageId)
    || commercialState.packageCoverageAmount > 0;
}

export async function getOrganizationBookings(db, organizationId = '', { startAfter, endBefore } = {}) {
  if (!organizationId) return [];

  const records = (await loadRecordsByEquality(db, {
    collectionName: PLATFORM_COLLECTIONS.BOOKINGS,
    field: 'organizationId',
    value: organizationId,
  })) || [];

  let results = records;

  // If a date window is provided, filter client-side for performance
  if (startAfter || endBefore) {
    const startMs = startAfter ? new Date(startAfter).getTime() : 0;
    const endMs = endBefore ? new Date(endBefore).getTime() : Infinity;

    results = records.filter((r) => {
      const ts = r.startAt;
      if (!ts) return false;
      const ms = ts.toDate ? ts.toDate().getTime()
        : ts.seconds ? ts.seconds * 1000
        : new Date(ts).getTime();
      return ms >= startMs && ms <= endMs;
    });
  }

  return results.map((record) => normalizeBooking(record));
}

/**
 * Create a manual operations schedule entry (canonical booking). Records a
 * `scheduleChanges` event and, when assigned, notifies the instructor. This is
 * the single writer the ops "New lesson" form uses — UI components must not
 * addDoc bookings directly.
 */
export async function createManualBooking(db, {
  organizationId = '',
  // Pass 82C — the org runtime context (the org doc / selected-org record). Generic timezone/season come from HERE,
  // not a hidden SAS default. Optional so an explicit `timezone` arg (e.g. an ops form building JST wall-clock) still wins.
  organization = null,
  instructorId = '',
  instructorName = '',
  requesterUserId = '',
  requesterName = '',
  requesterEmail = '',
  serviceName = '',
  discipline = '',
  serviceType = '',
  startAt = null,
  endAt = null,
  // Pass 82C — no SAS default here. An explicit timezone (org-derived by the caller) wins; otherwise resolve from the
  // organization runtime; otherwise a neutral 'UTC'. SAS callers pass Asia/Tokyo (or the SAS org doc carries it).
  timezone = '',
  locationLabel = '',
  // Pass 82E — structured operating-area / service-location / meeting-point + meeting type on a Motus-native booking.
  // A native booking MUST carry an explicit operatingAreaId (the multi-area cutover; never a silent Niseko default).
  operatingAreaId = '',
  serviceLocationId = '',
  meetingPointId = '',
  meetingType = '',
  // Only enforce the explicit-area requirement for an org that actually operates areas (config present). An
  // unconfigured org (e.g. the pilot) keeps a graceful area-less state — no silent default, no forced field.
  enforceOperatingArea = false,
  groupSize = null,
  language = '',
  transportInfo = '',
  notes = '',
  internalNotes = '',
  actorUserId = '',
  actorName = '',
  instructorAuthUserId = '', // Pass 49A §7: linked Auth UID for an assigned-on-create booking/event
  activityType = '',         // §7/§13 Add Work: lesson|driving|concierge|admin|...
  // Pass 49B §4: canonical non-lesson operational events (Add Work) must be able to set BOTH
  // the season and the schedule entry type explicitly. Accepted additively; when omitted the
  // legacy defaults apply (scheduleEntryType → normalizeEntryType default 'client_booking').
  seasonId = '',
  scheduleEntryType = '',
  // Pass 43: additive workbook fields (incl. scheduleEntryType for internal events).
  extraFields = {},
  // Pass 57C (item 12): normalized authorization for Add Work.
  userData = null,
} = {}) {
  if (!organizationId) throw new Error('Organisation is required.');
  requireOrgWriteActor({ userData, organizationId, action: 'assignment' });
  // Pass 82E §13/§36 — a NEW Motus-native booking in an area-operating org must carry an explicit operating area (fail
  // closed; never a silent default). Enforced only when the org operates areas (`enforceOperatingArea`), so an
  // unconfigured tenant keeps a graceful area-less state.
  if (enforceOperatingArea) assertExplicitAreaForNewRecord(operatingAreaId, { isSourceBacked: false });
  if (!startAt || !endAt) throw new Error('Start and finish time are required.');
  if (new Date(endAt).getTime() <= new Date(startAt).getTime()) {
    throw new Error('Finish time must be after start time.');
  }

  // §3/§7/§review2: the Auth UID is DERIVED from the canonical organizationMember, never
  // trusted from the caller. A caller-supplied instructorAuthUserId is honoured only if it
  // exactly matches the resolved member UID; otherwise it is ignored (never attach another
  // user's Auth UID to a booking).
  const canonicalAuthUid = instructorId ? await resolveMemberAuthUserId(db, organizationId, instructorId) : '';
  if (instructorAuthUserId && instructorAuthUserId !== canonicalAuthUid) {
    console.warn('Ignoring caller-supplied instructorAuthUserId that does not match the canonical member', { organizationId, instructorId });
  }
  const resolvedAuthUid = canonicalAuthUid;

  // Pass 82C — timezone is org-derived (explicit arg wins), never a hidden SAS default in this generic writer.
  const resolvedTimezone = timezone || resolveOrgTimezone(organization);
  const payload = normalizeBooking({
    organizationId,
    instructorId,
    instructorAuthUserId: resolvedAuthUid || null,
    activityType,
    // §4: only set scheduleEntryType when supplied, so we don't override an explicit
    // extraFields.scheduleEntryType and the legacy default still applies when omitted.
    ...(scheduleEntryType ? { scheduleEntryType } : {}),
    ...(seasonId ? { seasonId } : {}),
    instructorNameSnapshot: instructorName,
    requesterUserId,
    requesterName,
    requesterEmail,
    serviceNameSnapshot: serviceName,
    serviceType: serviceType || serviceName,
    discipline,
    startAt,
    endAt,
    timezone: resolvedTimezone,
    status: 'confirmed',
    locationLabel,
    // Pass 82E — structured location on the native booking (explicit; empty ids are valid for the non-area fields).
    operatingAreaId,
    serviceLocationId,
    meetingPointId,
    ...(meetingType ? { meetingType } : {}),
    groupSize,
    language,
    transportInfo,
    notes,
    // Pass 82C — tenant-neutral canonical manual source (was 'manual_sas'). WHO owns it = organizationId; HOW it
    // entered Motus = 'manual'. Organization identity is never encoded into the source vocabulary.
    source: MANUAL_BOOKING_SOURCE,
    sourceType: MANUAL_BOOKING_SOURCE,
    assignmentSource: instructorId ? 'org_direct' : '',
    ...extraFields,
  });

  const ref = await addDoc(collection(db, PLATFORM_COLLECTIONS.BOOKINGS), {
    ...payload,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  const bookingId = ref.id;
  const savedBooking = { ...payload, bookingId, id: bookingId };

  if (internalNotes) {
    await setBookingInternalNotes(db, { bookingId, organizationId, internalNotes, updatedByUserId: actorUserId }).catch(() => {});
  }

  try {
    if (instructorId) {
      // Assigned on creation → audit as "assigned" + notify the instructor.
      await recordAndNotifyReassignment(db, {
        booking: savedBooking, previousInstructorId: '', newInstructorId: instructorId, newInstructorAuthUserId: resolvedAuthUid || null, actorUserId, actorName,
      });
    } else {
      const { recordScheduleChange } = await import('./scheduleChanges');
      await recordScheduleChange(db, {
        bookingId, organizationId, changeType: SCHEDULE_CHANGE_TYPES.CREATED,
        changes: [{ field: 'status', label: 'Created', kind: 'text', previousValue: '', newValue: 'confirmed' }],
        changedByUserId: actorUserId, changedByName: actorName,
      });
    }
  } catch (err) {
    console.warn('Manual booking audit/notify failed:', err);
  }

  return savedBooking;
}

// ── Staff-only internal notes (bookingInternal/{bookingId}) ──────────────────
// Firestore has no field-level read security, so operational notes that clients
// must never see live in a separate doc guarded by staff/assigned-instructor rules.
export async function getBookingInternal(db, bookingId = '') {
  if (!bookingId) return null;
  const snap = await getDoc(doc(db, 'bookingInternal', bookingId));
  return snap?.exists?.() ? { id: snap.id, ...snap.data() } : null;
}

export async function setBookingInternalNotes(db, { bookingId = '', organizationId = '', internalNotes = '', updatedByUserId = '' } = {}) {
  if (!bookingId) throw new Error('Booking id is required.');
  await setDoc(
    doc(db, 'bookingInternal', bookingId),
    { bookingId, organizationId, internalNotes, updatedByUserId, updatedAt: serverTimestamp() },
    { merge: true }
  );
  return { bookingId };
}

// Pass 82A.1 (blocker 6) — organizationId is REQUIRED and the query filters by BOTH organizationId AND instructorId,
// so a single business/instructor id that appears in more than one organization can never blend bookings across orgs.
// Missing org (or instructor) fails closed → []. Callers MUST pass the authoritative current organization (never a
// 'sas-niseko' fallback).
export async function getInstructorBookings(db, organizationId = '', instructorId = '') {
  if (!organizationId || !instructorId) return [];
  const records = (await loadRecordsByEquality(db, {
    collectionName: PLATFORM_COLLECTIONS.BOOKINGS,
    field: 'organizationId',
    value: organizationId,
    andField: 'instructorId',
    andValue: instructorId,
  })) || [];
  return records.map((record) => normalizeBooking(record));
}

export async function getRequesterBookings(db, requesterUserId = '') {
  if (!requesterUserId) return [];
  const records = (await loadRecordsByEquality(db, {
    collectionName: PLATFORM_COLLECTIONS.BOOKINGS,
    field: 'requesterUserId',
    value: requesterUserId,
  })) || [];
  return records.map((record) => normalizeBooking(record));
}

export async function getUpcomingBookings(db, { organizationId = '', instructorId = '', requesterUserId = '' } = {}) {
  // Pass 82A.1 — an instructor-scoped read is org-scoped (getInstructorBookings requires org). Check instructorId first
  // so an instructor call (org + instructorId) is filtered to that instructor; a pure org call (no instructorId) still
  // gets the whole-org view.
  const records = instructorId
    ? await getInstructorBookings(db, organizationId, instructorId)
    : organizationId
      ? await getOrganizationBookings(db, organizationId)
      : requesterUserId
        ? await getRequesterBookings(db, requesterUserId)
        : [];

  const now = Date.now();
  return records.filter((booking) => {
    const start = toDateValue(booking.startAt)?.getTime?.() || 0;
    return ['confirmed', 'upcoming'].includes(booking.status) && start >= now;
  });
}

export async function getUpcomingBookingsForUser(
  db,
  { currentUserId = '', role = 'student', organizationId = '' } = {}
) {
  if (!currentUserId && !organizationId) return [];

  if (role === 'instructor') {
    // Pass 82A.1 — thread org so the instructor's bookings are tenant-scoped (fails closed without org).
    return getUpcomingBookings(db, { organizationId, instructorId: currentUserId });
  }

  if (role === 'organization_admin' || role === 'organization_owner') {
    return getUpcomingBookings(db, { organizationId });
  }

  return getUpcomingBookings(db, { requesterUserId: currentUserId });
}

export function getBookingsRequiringFollowUp(bookings = []) {
  return (bookings || [])
    .map((booking) => normalizeBooking(booking))
    .filter((booking) => booking.status === 'completed')
    .filter((booking) => !booking.videoId || !booking.feedbackId || !booking.aiReviewId);
}

export function getConflictingBookings(bookings = [], candidateBooking = {}, { excludeBookingId = '' } = {}) {
  const normalizedCandidate = normalizeBooking(candidateBooking, candidateBooking.bookingId || candidateBooking.id || '');
  return (bookings || [])
    .map((booking) => normalizeBooking(booking))
    .filter((booking) => ['confirmed', 'rescheduled'].includes(booking.status))
    .filter((booking) => booking.bookingId !== excludeBookingId && booking.bookingId !== normalizedCandidate.bookingId)
    .filter((booking) => booking.instructorId === normalizedCandidate.instructorId)
    .filter((booking) => rangesOverlap(
      booking.startAt,
      booking.endAt,
      normalizedCandidate.startAt,
      normalizedCandidate.endAt
    ));
}

export async function hasBookingConflict(db, candidateBooking = {}, options = {}) {
  // Pass 82A.1 — conflict detection is org-scoped (the candidate booking carries its organizationId).
  const bookings = await getInstructorBookings(db, candidateBooking.organizationId || '', candidateBooking.instructorId || '');
  return getConflictingBookings(bookings, candidateBooking, options).length > 0;
}

export async function canConfirmBookingForSlot(
  db,
  {
    bookingRequest = {},
    startAt = bookingRequest?.requestedStartAt || null,
    endAt = bookingRequest?.requestedEndAt || null,
  } = {}
) {
  const instructorId = bookingRequest?.instructorId || '';
  if (!instructorId) {
    return { ok: true, reason: '' };
  }

  const candidateBooking = normalizeBooking({
    instructorId,
    // Pass 82A.1 — thread the request's org so the (now org-scoped) conflict check queries the right tenant.
    organizationId: bookingRequest?.organizationId || '',
    startAt,
    endAt,
    timezone: bookingRequest?.timezone || 'UTC',
  });

  if (await hasBookingConflict(db, candidateBooking)) {
    return { ok: false, reason: 'This instructor already has a confirmed booking in that window.' };
  }

  const availability = await getInstructorAvailability(db, instructorId);
  const activeAvailability = availability.filter((slot) => slot.status === 'active');
  if (activeAvailability.length === 0) {
    return { ok: true, reason: '' };
  }

  const fitsAvailability = activeAvailability.some((slot) =>
    bookingWindowFitsAvailability({
      requestedStartAt: startAt,
      requestedEndAt: endAt,
      timezone: bookingRequest?.timezone || 'UTC',
    }, slot)
  );

  return fitsAvailability
    ? { ok: true, reason: '' }
    : { ok: false, reason: 'The requested time does not match active instructor availability.' };
}

async function findDuplicatePendingBookingRequest(
  db,
  {
    organizationId = '',
    instructorId = '',
    requesterEmail = '',
    requestedStartAt = null,
  } = {}
) {
  const candidateRecords = organizationId
    ? await loadRecordsByEquality(db, {
        collectionName: PLATFORM_COLLECTIONS.BOOKING_REQUESTS,
        field: 'organizationId',
        value: organizationId,
      })
    : instructorId
      ? await loadRecordsByEquality(db, {
          collectionName: PLATFORM_COLLECTIONS.BOOKING_REQUESTS,
          field: 'instructorId',
          value: instructorId,
        })
      : [];

  const email = normalizeEmail(requesterEmail);
  const requestedStartValue = requestedStartAt?.toMillis?.() || requestedStartAt?.seconds || requestedStartAt || '';

  return candidateRecords
    .map((record) => normalizeBookingRequest(record))
    .find((record) => {
      const recordStartValue = record.requestedStartAt?.toMillis?.() || record.requestedStartAt?.seconds || record.requestedStartAt || '';
      return record.status === 'pending'
        && record.requesterEmailNormalized === email
        && record.organizationId === organizationId
        && record.instructorId === instructorId
        && String(recordStartValue) === String(requestedStartValue);
    }) || null;
}

export async function createBookingRequest(
  db,
  {
    organizationId = '',
    instructorId = '',
    requesterUserId = '',
    clientId = '', // Pass 70A: canonical commercial client identity (present for a no-Auth customer)
    requesterName = '',
    requesterEmail = '',
    requesterPhone = '',
    serviceId = '',
    serviceNameSnapshot = '',
    serviceDurationSnapshot = null,
    servicePricingSnapshot = null,
    servicePackageEligible = false,
    applicablePackageIds = [],
    discipline = '',
    serviceType = '',
    requestedStartAt = null,
    requestedEndAt = null,
    timezone = 'UTC',
    notes = '',
    sourceType = 'public_profile',
    actorUserId = '',
    actorName = '',
  } = {}
) {
  const duplicate = await findDuplicatePendingBookingRequest(db, {
    organizationId,
    instructorId,
    requesterEmail,
    requestedStartAt,
  });

  if (duplicate) {
    return { ok: true, duplicate: true, bookingRequest: duplicate };
  }

  const payload = normalizeBookingRequest({
    organizationId,
    instructorId,
    requesterUserId,
    requesterName,
    requesterEmail,
    requesterPhone,
    serviceId,
    serviceNameSnapshot,
    serviceDurationSnapshot,
    servicePricingSnapshot,
    servicePackageEligible,
    applicablePackageIds,
    discipline,
    serviceType,
    requestedStartAt,
    requestedEndAt,
    timezone,
    status: 'pending',
    notes,
    sourceType,
  });

  // Pass 70A — persist the canonical commercial clientId alongside the normalized request so a no-Auth
  // customer's rebook is linked by clientId (normalizeBookingRequest carries requester fields, not clientId).
  const docPayload = { ...payload, ...(clientId ? { clientId } : {}) };
  const docRef = (await addDoc(collection(db, PLATFORM_COLLECTIONS.BOOKING_REQUESTS), {
    ...docPayload,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })) || {};

  const bookingRequest = normalizeBookingRequest(docPayload, docRef.id || '');

  try {
    await notifyBookingRequestCreated(db, {
      bookingRequest,
      actorUserId,
      actorName,
    });
  } catch (error) {
    console.warn('Booking request notification failed:', error);
  }

  return { ok: true, duplicate: false, bookingRequest };
}

export async function getOrganizationBookingRequests(db, organizationId = '') {
  if (!organizationId) return [];
  const records = await loadRecordsByEquality(db, {
    collectionName: PLATFORM_COLLECTIONS.BOOKING_REQUESTS,
    field: 'organizationId',
    value: organizationId,
  });
  return records.map((record) => normalizeBookingRequest(record));
}

export async function getInstructorBookingRequests(db, instructorId = '') {
  if (!instructorId) return [];
  const records = await loadRecordsByEquality(db, {
    collectionName: PLATFORM_COLLECTIONS.BOOKING_REQUESTS,
    field: 'instructorId',
    value: instructorId,
  });
  return records.map((record) => normalizeBookingRequest(record));
}

export async function getRequesterBookingRequests(db, requesterUserId = '') {
  if (!requesterUserId) return [];
  const records = await loadRecordsByEquality(db, {
    collectionName: PLATFORM_COLLECTIONS.BOOKING_REQUESTS,
    field: 'requesterUserId',
    value: requesterUserId,
  });
  return records.map((record) => normalizeBookingRequest(record));
}

async function updateBookingRequestStatus(
  db,
  bookingRequestId = '',
  {
    status = 'pending',
    handledByUserId = '',
    handledAt = serverTimestamp(),
    externalCalendarRef = '',
    bookingId = '',
  } = {}
) {
  if (!bookingRequestId) throw new Error('Booking request id is required.');

  await updateDoc(doc(db, PLATFORM_COLLECTIONS.BOOKING_REQUESTS, bookingRequestId), {
    status,
    handledByUserId,
    handledAt,
    externalCalendarRef,
    bookingId,
    updatedAt: serverTimestamp(),
  });

  return {
    bookingRequestId,
    status,
    handledByUserId,
    handledAt,
    externalCalendarRef,
    bookingId,
  };
}

export async function acceptBookingRequest(db, { bookingRequest = {}, handledByUserId = '', actorUserId = '', actorName = '', externalCalendarRef = '' } = {}) {
  const result = await updateBookingRequestStatus(db, bookingRequest.bookingRequestId || bookingRequest.id, {
    status: 'accepted',
    handledByUserId,
    handledAt: serverTimestamp(),
    externalCalendarRef,
  });
  try {
    await notifyBookingRequestAccepted(db, { bookingRequest, actorUserId, actorName });
  } catch (error) {
    console.warn('Booking accepted notification failed:', error);
  }
  return result;
}

export async function declineBookingRequest(db, { bookingRequest = {}, handledByUserId = '', actorUserId = '', actorName = '' } = {}) {
  const result = await updateBookingRequestStatus(db, bookingRequest.bookingRequestId || bookingRequest.id, {
    status: 'declined',
    handledByUserId,
    handledAt: serverTimestamp(),
  });
  try {
    await notifyBookingRequestDeclined(db, { bookingRequest, actorUserId, actorName });
  } catch (error) {
    console.warn('Booking declined notification failed:', error);
  }
  return result;
}

export async function cancelBookingRequest(db, { bookingRequest = {}, handledByUserId = '', actorUserId = '', actorName = '' } = {}) {
  const result = await updateBookingRequestStatus(db, bookingRequest.bookingRequestId || bookingRequest.id, {
    status: 'canceled',
    handledByUserId,
    handledAt: serverTimestamp(),
  });
  try {
    await notifyBookingRequestCanceled(db, { bookingRequest, actorUserId, actorName });
  } catch (error) {
    console.warn('Booking canceled notification failed:', error);
  }
  return result;
}

export async function completeBookingRequest(db, { bookingRequest = {}, handledByUserId = '', actorUserId = '', actorName = '' } = {}) {
  const result = await updateBookingRequestStatus(db, bookingRequest.bookingRequestId || bookingRequest.id, {
    status: 'completed',
    handledByUserId,
    handledAt: serverTimestamp(),
  });
  try {
    await notifyBookingRequestCompleted(db, { bookingRequest, actorUserId, actorName });
  } catch (error) {
    console.warn('Booking completed notification failed:', error);
  }
  return result;
}

export async function createBookingFromRequest(
  db,
  {
    bookingRequest = {},
    handledByUserId = '',
    actorUserId = '',
    actorName = '',
    startAt = bookingRequest?.requestedStartAt || null,
    endAt = bookingRequest?.requestedEndAt || null,
    locationLabel = '',
    meetingType = 'in_person',
    externalCalendarRef = '',
    externalEventId = '',
    calendarProvider = '',
    syncStatus = 'not_synced',
    priceSnapshot = null,
    currency = 'USD',
    paymentStatus = 'unpaid',
    billingSourceType = '',
    billingSourceId = '',
    packageId = '',
    depositRequired = false,
    depositStatus = '',
  } = {}
) {
  const normalizedRequest = normalizeBookingRequest(bookingRequest);
  if (!normalizedRequest.bookingRequestId) {
    throw new Error('Booking request id is required.');
  }

  if (!['accepted', 'confirmed'].includes(normalizedRequest.status)) {
    throw new Error('Only accepted booking requests can be confirmed.');
  }

  const existingBookings = await loadRecordsByEquality(db, {
    collectionName: PLATFORM_COLLECTIONS.BOOKINGS,
    field: 'bookingRequestId',
    value: normalizedRequest.bookingRequestId,
  });
  if ((existingBookings || []).length > 0) {
    return { ok: true, duplicate: true, booking: normalizeBooking(existingBookings[0]) };
  }

  const validation = await canConfirmBookingForSlot(db, {
    bookingRequest: normalizedRequest,
    startAt,
    endAt,
  });
  if (!validation.ok) {
    throw new Error(validation.reason);
  }

  const bookingPayload = normalizeBooking({
    organizationId: normalizedRequest.organizationId,
    instructorId: normalizedRequest.instructorId,
    requesterUserId: normalizedRequest.requesterUserId,
    requesterName: normalizedRequest.requesterName,
    requesterEmail: normalizedRequest.requesterEmail,
    requesterPhone: normalizedRequest.requesterPhone,
    bookingRequestId: normalizedRequest.bookingRequestId,
    serviceId: normalizedRequest.serviceId,
    serviceNameSnapshot: normalizedRequest.serviceNameSnapshot,
    serviceDurationSnapshot: normalizedRequest.serviceDurationSnapshot,
    servicePricingSnapshot: normalizedRequest.servicePricingSnapshot,
    servicePackageEligible: normalizedRequest.servicePackageEligible,
    applicablePackageIds: normalizedRequest.applicablePackageIds,
    discipline: normalizedRequest.discipline,
    serviceType: normalizedRequest.serviceType,
    startAt,
    endAt,
    timezone: normalizedRequest.timezone,
    status: 'confirmed',
    notes: normalizedRequest.notes,
    locationLabel,
    meetingType,
    sourceType: normalizedRequest.sourceType,
    handledByUserId,
    lessonId: '',
    videoId: '',
    feedbackId: '',
    aiReviewId: '',
    linkedDrillIds: [],
    followUpRecommended: false,
    followUpBookingId: '',
    externalCalendarRef,
    externalEventId,
    calendarProvider,
    syncStatus,
    syncAttemptCount: 0,
    lastSyncedAt: null,
    syncErrorMessage: '',
    priceSnapshot: priceSnapshot ?? normalizedRequest.servicePricingSnapshot,
    currency,
    paymentStatus,
    billingSourceType,
    billingSourceId,
    packageId,
    depositRequired,
    depositStatus,
  });

  const docRef = (await addDoc(collection(db, PLATFORM_COLLECTIONS.BOOKINGS), {
    ...bookingPayload,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })) || {};

  const booking = normalizeBooking(bookingPayload, docRef.id || '');
  await updateBookingRequestStatus(db, normalizedRequest.bookingRequestId, {
    status: 'confirmed',
    handledByUserId,
    handledAt: serverTimestamp(),
    externalCalendarRef,
    bookingId: booking.bookingId,
  });

  try {
    await notifyBookingConfirmed(db, {
      booking,
      actorUserId,
      actorName,
    });
  } catch (error) {
    console.warn('Booking confirmation notification failed:', error);
  }

  return { ok: true, duplicate: false, booking };
}

export async function updateBooking(
  db,
  bookingId = '',
  {
    startAt = null,
    endAt = null,
    timezone = '',
    locationLabel = '',
    meetingType = '',
    notes = '',
    internalNotes = '',
    groupSize = null,
    language = '',
    transportInfo = '',
    externalCalendarRef = '',
    externalEventId = '',
    calendarProvider = '',
    syncStatus = '',
    actorUserId = '',
    actorName = '',
    // Pass 43: additive SAS-workbook fields (scheduleEntryType, bookingType,
    // skillLevel, discipline, paymentState, cancellationResolution, leadGuest,
    // additionalGuests, accommodation*, roomBossId, invoiceNumber, isReferral,
    // referredBy*, completedHours, rateType, payExtras). Provided fields override.
    extraFields = {},
  } = {}
) {
  if (!bookingId) throw new Error('Booking id is required.');

  const existingSnapshot = await getDoc(doc(db, PLATFORM_COLLECTIONS.BOOKINGS, bookingId));
  const existingBooking = existingSnapshot?.exists?.()
    ? normalizeBooking({ id: existingSnapshot.id, ...existingSnapshot.data() })
    : normalizeBooking({ bookingId: bookingId });
  const nextBooking = normalizeBooking({
    ...existingBooking,
    ...extraFields,
    startAt: startAt || existingBooking.startAt,
    endAt: endAt || existingBooking.endAt,
    timezone: timezone || existingBooking.timezone,
    locationLabel: locationLabel || existingBooking.locationLabel,
    meetingType: meetingType || existingBooking.meetingType,
    notes: notes || existingBooking.notes,
    internalNotes: internalNotes || existingBooking.internalNotes,
    groupSize: groupSize != null ? groupSize : existingBooking.groupSize,
    language: language || existingBooking.language,
    transportInfo: transportInfo || existingBooking.transportInfo,
    externalCalendarRef: externalCalendarRef || existingBooking.externalCalendarRef,
    externalEventId: externalEventId || existingBooking.externalEventId,
    calendarProvider: calendarProvider || existingBooking.calendarProvider,
    syncStatus: syncStatus || (existingBooking.calendarProvider || existingBooking.externalCalendarRef || existingBooking.externalEventId ? 'pending' : existingBooking.syncStatus),
    // Preserve cancelled state on edit rather than silently re-confirming a
    // cancelled lesson. Restoration is an explicit action (restoreBooking).
    status: existingBooking.status === 'canceled' ? 'canceled' : 'confirmed',
  }, bookingId);

  if (nextBooking.status !== 'canceled' && await hasBookingConflict(db, nextBooking, { excludeBookingId: bookingId })) {
    throw new Error('Booking update conflicts with another confirmed booking.');
  }

  // NOTE: internalNotes is deliberately NOT written to the booking doc (clients
  // can read the booking). It lives in the staff-only `bookingInternal/{id}` doc.
  await updateDoc(doc(db, PLATFORM_COLLECTIONS.BOOKINGS, bookingId), {
    startAt: nextBooking.startAt,
    endAt: nextBooking.endAt,
    timezone: nextBooking.timezone,
    locationLabel: nextBooking.locationLabel,
    meetingType: nextBooking.meetingType,
    notes: nextBooking.notes,
    groupSize: nextBooking.groupSize,
    language: nextBooking.language,
    transportInfo: nextBooking.transportInfo,
    externalCalendarRef: nextBooking.externalCalendarRef,
    externalEventId: nextBooking.externalEventId,
    calendarProvider: nextBooking.calendarProvider,
    syncStatus: nextBooking.syncStatus,
    // Pass 43 additive workbook fields
    scheduleEntryType: nextBooking.scheduleEntryType,
    bookingType: nextBooking.bookingType,
    durationCategory: nextBooking.durationCategory,
    skillLevel: nextBooking.skillLevel,
    discipline: nextBooking.discipline,
    serviceType: nextBooking.serviceType,
    serviceNameSnapshot: nextBooking.serviceNameSnapshot,
    paymentState: nextBooking.paymentState,
    cancellationResolution: nextBooking.cancellationResolution,
    leadGuest: nextBooking.leadGuest,
    additionalGuests: nextBooking.additionalGuests,
    requesterName: nextBooking.leadGuest.fullName || nextBooking.requesterName,
    requesterEmail: nextBooking.leadGuest.email || nextBooking.requesterEmail,
    requesterPhone: nextBooking.leadGuest.phone || nextBooking.requesterPhone,
    accommodationName: nextBooking.accommodationName,
    accommodationAddress: nextBooking.accommodationAddress,
    accommodationMapUrl: nextBooking.accommodationMapUrl,
    roomBossId: nextBooking.roomBossId,
    invoiceNumber: nextBooking.invoiceNumber,
    isReferral: nextBooking.isReferral,
    referredByName: nextBooking.referredByName,
    referredByInstructorId: nextBooking.referredByInstructorId,
    completedHours: nextBooking.completedHours,
    rateType: nextBooking.rateType,
    payExtras: nextBooking.payExtras,
    updatedAt: serverTimestamp(),
  });

  // Staff-only operational notes → protected doc.
  if (internalNotes !== '') {
    await setBookingInternalNotes(db, {
      bookingId,
      organizationId: nextBooking.organizationId,
      internalNotes,
      updatedByUserId: actorUserId,
    }).catch((err) => console.warn('bookingInternal write failed:', err));
  }

  // Canonical audit + instructor notification for material schedule changes.
  try {
    await recordAndNotifyBookingEdit(db, {
      previousBooking: existingBooking,
      nextBooking,
      actorUserId,
      actorName,
    });
  } catch (error) {
    console.warn('Schedule-change audit/notify failed:', error);
  }

  // Existing client-facing reschedule notification (unchanged).
  try {
    await notifyBookingRescheduled(db, {
      booking: nextBooking,
      actorUserId,
      actorName,
    });
  } catch (error) {
    console.warn('Booking reschedule notification failed:', error);
  }

  return nextBooking;
}

async function updateBookingFields(db, bookingId = '', fields = {}) {
  if (!bookingId) throw new Error('Booking id is required.');
  await updateDoc(doc(db, PLATFORM_COLLECTIONS.BOOKINGS, bookingId), {
    ...fields,
    updatedAt: serverTimestamp(),
  });
}

export async function markBookingInProgress(db, { booking = {}, handledByUserId = '', actorUserId = '', actorName = '' } = {}) {
  const normalizedBooking = normalizeBooking(booking);
  await updateBookingFields(db, normalizedBooking.bookingId, {
    status: 'in_progress',
    handledByUserId,
  });

  return { bookingId: normalizedBooking.bookingId, status: 'in_progress', actorUserId, actorName };
}

export async function markBookingCompleted(
  db,
  {
    booking = {},
    handledByUserId = '',
    actorUserId = '',
    actorName = '',
    followUpRecommended = true,
  } = {}
) {
  const result = await completeBooking(db, { booking, handledByUserId, actorUserId, actorName });
  await updateBookingFields(db, normalizeBooking(booking).bookingId, {
    followUpRecommended,
  });

  try {
    await notifyPostSessionUploadPrompt(db, {
      booking: {
        ...normalizeBooking(booking),
        followUpRecommended,
      },
      actorUserId,
      actorName,
    });
  } catch (error) {
    console.warn('Post-session upload prompt failed:', error);
  }

  return result;
}

export async function markBookingNoShow(db, { booking = {}, handledByUserId = '', actorUserId = '', actorName = '' } = {}) {
  const normalizedBooking = normalizeBooking(booking);
  await updateBookingFields(db, normalizedBooking.bookingId, {
    status: 'no_show',
    handledByUserId,
  });

  try {
    await notifyBookingNoShow(db, { booking: normalizedBooking, actorUserId, actorName });
  } catch (error) {
    console.warn('Booking no-show notification failed:', error);
  }

  return { bookingId: normalizedBooking.bookingId, status: 'no_show' };
}

export async function createSessionVideoFromBooking(
  db,
  {
    booking = {},
    requesterUserId = '',
    actorUserId = '',
    actorName = '',
  } = {}
) {
  const normalizedBooking = normalizeBooking(booking);
  if (!normalizedBooking.bookingId) throw new Error('Booking id is required.');

  // Pass 72 (§35) — VIDEO assignment is an Auth uid. Use the booking's resolved `instructorAuthUserId`, NOT the
  // business `instructorId` (which the old code wrongly wrote into the video's Auth-assignment fields). If the
  // assigned instructor has no Auth linkage yet, leave the video UNASSIGNED rather than fabricate an identity.
  const instructorAuthUserId = normalizedBooking.instructorAuthUserId || '';
  const payload = {
    userId: requesterUserId || normalizedBooking.requesterUserId || '',
    studentId: requesterUserId || normalizedBooking.requesterUserId || '',
    assignedInstructorId: instructorAuthUserId,
    assignedTo: instructorAuthUserId || null,
    assigned: Boolean(instructorAuthUserId),
    organizationId: normalizedBooking.organizationId || '',
    bookingId: normalizedBooking.bookingId,
    bookingRequestId: normalizedBooking.bookingRequestId || '',
    videoName: normalizedBooking.serviceType || 'Session Upload',
    sport: normalizedBooking.discipline || '',
    coachType: 'real',
    status: 'pending',
    uploadStatus: 'pending',
    bookingLinkedAt: serverTimestamp(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const docRef = (await addDoc(collection(db, 'videos'), payload)) || {};
  const videoId = docRef.id || '';

  await updateBookingFields(db, normalizedBooking.bookingId, {
    videoId,
  });

  try {
    await notifyBookingFollowUpRequested(db, {
      booking: {
        ...normalizedBooking,
        videoId,
      },
      actorUserId,
      actorName,
    });
  } catch (error) {
    console.warn('Booking follow-up notification failed:', error);
  }

  return { ok: true, videoId };
}

export async function createLessonShellFromBooking(
  db,
  {
    booking = {},
    actorUserId = '',
    actorName = '',
  } = {}
) {
  const normalizedBooking = normalizeBooking(booking);
  if (!normalizedBooking.bookingId) {
    throw new Error('Booking id is required.');
  }

  if (normalizedBooking.lessonId) {
    return { ok: true, duplicate: true, lessonId: normalizedBooking.lessonId };
  }

  const payload = {
    organizationId: normalizedBooking.organizationId || '',
    bookingId: normalizedBooking.bookingId,
    bookingRequestId: normalizedBooking.bookingRequestId || '',
    sourceType: 'booking',
    assignmentSource: 'booking',
    studentId: normalizedBooking.requesterUserId || '',
    studentName: normalizedBooking.requesterName || '',
    studentEmail: normalizedBooking.requesterEmail || '',
    studentEmailNormalized: normalizeEmail(normalizedBooking.requesterEmail || ''),
    instructorId: normalizedBooking.instructorId || '',
    assignedInstructorId: normalizedBooking.instructorId || '',
    sport: normalizedBooking.discipline || '',
    status: 'draft',
    targetSkillId: '',
    targetSkillName: '',
    title: normalizedBooking.serviceType || 'Session Follow-Up',
    summary: normalizedBooking.notes || '',
    bookingSeededAt: serverTimestamp(),
    createdByUserId: actorUserId || '',
    createdByName: actorName || '',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const lessonRef = (await addDoc(collection(db, 'lessonInstances'), payload)) || {};
  const lessonId = lessonRef.id || '';

  await updateBookingFields(db, normalizedBooking.bookingId, {
    lessonId,
  });

  return { ok: true, duplicate: false, lessonId };
}

export async function prepareBookingFollowUpWorkspace(
  db,
  {
    booking = {},
    seedLesson = true,
    seedVideo = true,
    requesterUserId = '',
    actorUserId = '',
    actorName = '',
  } = {}
) {
  const normalizedBooking = normalizeBooking(booking);
  let lessonId = normalizedBooking.lessonId || '';
  let videoId = normalizedBooking.videoId || '';

  if (seedLesson && !lessonId) {
    const lessonResult = await createLessonShellFromBooking(db, {
      booking: normalizedBooking,
      actorUserId,
      actorName,
    });
    lessonId = lessonResult.lessonId || '';
  }

  if (seedVideo && !videoId) {
    const videoResult = await createSessionVideoFromBooking(db, {
      booking: normalizedBooking,
      requesterUserId,
      actorUserId,
      actorName,
    });
    videoId = videoResult.videoId || '';
  }

  return {
    bookingId: normalizedBooking.bookingId,
    lessonId,
    videoId,
  };
}

export async function attachLessonToBooking(
  db,
  {
    booking = {},
    lessonId = '',
  } = {}
) {
  const normalizedBooking = normalizeBooking(booking);
  if (!normalizedBooking.bookingId || !lessonId) {
    throw new Error('bookingId and lessonId are required.');
  }

  await updateBookingFields(db, normalizedBooking.bookingId, {
    lessonId,
  });

  return {
    bookingId: normalizedBooking.bookingId,
    lessonId,
  };
}

export async function attachFeedbackToBooking(
  db,
  {
    booking = {},
    videoId = '',
    feedbackId = '',
    actorUserId = '',
    actorName = '',
  } = {}
) {
  const normalizedBooking = normalizeBooking(booking);
  await updateBookingFields(db, normalizedBooking.bookingId, {
    videoId: videoId || normalizedBooking.videoId,
    feedbackId,
  });

  try {
    await notifyFeedbackReadyForBooking(db, {
      booking: {
        ...normalizedBooking,
        videoId: videoId || normalizedBooking.videoId,
        feedbackId,
      },
      actorUserId,
      actorName,
    });
  } catch (error) {
    console.warn('Feedback-ready notification failed:', error);
  }

  return {
    bookingId: normalizedBooking.bookingId,
    videoId: videoId || normalizedBooking.videoId,
    feedbackId,
  };
}

export async function generateAIReviewForBooking(
  db,
  {
    booking = {},
    videoId = '',
  } = {}
) {
  const normalizedBooking = normalizeBooking(booking);
  const effectiveVideoId = videoId || normalizedBooking.videoId;
  if (!effectiveVideoId) throw new Error('videoId is required to generate an AI review for a booking.');

  await generateAiReviewDraft(effectiveVideoId);
  await updateBookingFields(db, normalizedBooking.bookingId, {
    videoId: effectiveVideoId,
    aiReviewId: effectiveVideoId,
  });

  return {
    bookingId: normalizedBooking.bookingId,
    aiReviewId: effectiveVideoId,
  };
}

export async function assignDrillsFromBooking(
  db,
  {
    booking = {},
    drillIds = [],
    videoId = '',
    actorUserId = '',
    actorName = '',
  } = {}
) {
  const normalizedBooking = normalizeBooking(booking);
  const nextDrillIds = Array.from(new Set((drillIds || []).filter(Boolean)));

  await updateBookingFields(db, normalizedBooking.bookingId, {
    linkedDrillIds: nextDrillIds,
    videoId: videoId || normalizedBooking.videoId,
  });

  try {
    await notifyNextSessionRecommended(db, {
      booking: {
        ...normalizedBooking,
        linkedDrillIds: nextDrillIds,
        videoId: videoId || normalizedBooking.videoId,
      },
      actorUserId,
      actorName,
    });
  } catch (error) {
    console.warn('Drill-assignment notification failed:', error);
  }

  return {
    bookingId: normalizedBooking.bookingId,
    linkedDrillIds: nextDrillIds,
  };
}

export async function attachBookingPriceSnapshot(
  db,
  {
    booking = {},
    priceSnapshot = null,
    currency = 'USD',
    paymentStatus = 'unpaid',
    billingSourceType = '',
    billingSourceId = '',
    packageId = '',
    depositRequired = false,
    depositStatus = '',
    actorUserId = '',
    actorName = '',
  } = {}
) {
  const normalizedBooking = normalizeBooking(booking);
  const commercialState = normalizeBookingCommercialState({
    ...normalizedBooking,
    priceSnapshot,
    currency,
    paymentStatus,
    billingSourceType,
    billingSourceId,
    packageId,
    depositRequired,
    depositStatus,
  });

  await updateBookingFields(db, normalizedBooking.bookingId, commercialState);

  if (commercialState.paymentStatus === 'pending' || (commercialState.depositRequired && commercialState.depositStatus === 'pending')) {
    try {
      await notifyBookingPaymentPending(db, {
        booking: {
          ...normalizedBooking,
          ...commercialState,
        },
        actorUserId,
        actorName,
      });
    } catch (error) {
      console.warn('Booking payment-pending notification failed:', error);
    }
  }

  return {
    bookingId: normalizedBooking.bookingId,
    ...commercialState,
  };
}

export async function markBookingSyncPending(
  db,
  {
    booking = {},
    externalCalendarRef = '',
    externalEventId = '',
    calendarProvider = '',
  } = {}
) {
  const normalizedBooking = normalizeBooking(booking);
  const nextAttemptCount = (normalizedBooking.syncAttemptCount || 0) + 1;
  await updateBookingFields(db, normalizedBooking.bookingId, {
    syncStatus: 'pending',
    syncAttemptCount: nextAttemptCount,
    externalCalendarRef: externalCalendarRef || normalizedBooking.externalCalendarRef,
    externalEventId: externalEventId || normalizedBooking.externalEventId,
    calendarProvider: calendarProvider || normalizedBooking.calendarProvider,
    syncErrorMessage: '',
  });

  return {
    bookingId: normalizedBooking.bookingId,
    syncStatus: 'pending',
    syncAttemptCount: nextAttemptCount,
  };
}

export async function markBookingSyncSuccess(
  db,
  {
    booking = {},
    externalCalendarRef = '',
    externalEventId = '',
    calendarProvider = '',
  } = {}
) {
  const normalizedBooking = normalizeBooking(booking);
  await updateBookingFields(db, normalizedBooking.bookingId, {
    syncStatus: 'synced',
    externalCalendarRef: externalCalendarRef || normalizedBooking.externalCalendarRef,
    externalEventId: externalEventId || normalizedBooking.externalEventId,
    calendarProvider: calendarProvider || normalizedBooking.calendarProvider,
    lastSyncedAt: serverTimestamp(),
    syncErrorMessage: '',
  });

  return {
    bookingId: normalizedBooking.bookingId,
    syncStatus: 'synced',
  };
}

export async function markBookingSyncError(
  db,
  {
    booking = {},
    syncErrorMessage = '',
  } = {}
) {
  const normalizedBooking = normalizeBooking(booking);
  const nextAttemptCount = (normalizedBooking.syncAttemptCount || 0) + 1;
  await updateBookingFields(db, normalizedBooking.bookingId, {
    syncStatus: 'error',
    syncAttemptCount: nextAttemptCount,
    syncErrorMessage: normalizeString(syncErrorMessage || 'Unknown calendar sync error'),
  });

  return {
    bookingId: normalizedBooking.bookingId,
    syncStatus: 'error',
    syncAttemptCount: nextAttemptCount,
  };
}

export async function markBookingSyncDisabled(
  db,
  {
    booking = {},
    syncStatus = 'disabled',
    syncErrorMessage = '',
  } = {}
) {
  const normalizedBooking = normalizeBooking(booking);
  await updateBookingFields(db, normalizedBooking.bookingId, {
    syncStatus: normalizeString(syncStatus || 'disabled'),
    syncErrorMessage: normalizeString(syncErrorMessage || ''),
  });

  return {
    bookingId: normalizedBooking.bookingId,
    syncStatus: normalizeString(syncStatus || 'disabled'),
  };
}

export async function requestBookingCancellation(
  db,
  {
    booking = {},
    requesterUserId = '',
    requesterName = '',
    note = '',
    actorUserId = '',
    actorName = '',
  } = {}
) {
  const normalizedBooking = normalizeBooking(booking);
  const requesterChangeRequest = normalizeBookingChangeRequest({
    type: 'cancel',
    status: 'pending',
    requestedByUserId: requesterUserId || normalizedBooking.requesterUserId,
    requestedByName: requesterName || normalizedBooking.requesterName,
    note,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  await updateBookingFields(db, normalizedBooking.bookingId, {
    requesterChangeRequest,
  });

  try {
    await notifyBookingChangeRequested(db, {
      booking: {
        ...normalizedBooking,
        requesterChangeRequest,
      },
      actorUserId,
      actorName,
    });
  } catch (error) {
    console.warn('Booking cancellation request notification failed:', error);
  }

  return {
    bookingId: normalizedBooking.bookingId,
    requesterChangeRequest,
  };
}

export async function requestBookingReschedule(
  db,
  {
    booking = {},
    requestedStartAt = null,
    requestedEndAt = null,
    requesterUserId = '',
    requesterName = '',
    note = '',
    actorUserId = '',
    actorName = '',
  } = {}
) {
  const normalizedBooking = normalizeBooking(booking);
  const requesterChangeRequest = normalizeBookingChangeRequest({
    type: 'reschedule',
    status: 'pending',
    requestedStartAt,
    requestedEndAt,
    requestedByUserId: requesterUserId || normalizedBooking.requesterUserId,
    requestedByName: requesterName || normalizedBooking.requesterName,
    note,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  await updateBookingFields(db, normalizedBooking.bookingId, {
    requesterChangeRequest,
  });

  try {
    await notifyBookingChangeRequested(db, {
      booking: {
        ...normalizedBooking,
        requesterChangeRequest,
      },
      actorUserId,
      actorName,
    });
  } catch (error) {
    console.warn('Booking reschedule request notification failed:', error);
  }

  return {
    bookingId: normalizedBooking.bookingId,
    requesterChangeRequest,
  };
}

export async function respondToBookingChangeRequest(
  db,
  {
    booking = {},
    decision = 'declined',
    resolutionNote = '',
    handledByUserId = '',
    actorUserId = '',
    actorName = '',
  } = {}
) {
  const normalizedBooking = normalizeBooking(booking);
  const currentRequest = normalizeBookingChangeRequest(normalizedBooking.requesterChangeRequest || {});

  if (!currentRequest?.type || currentRequest.status !== 'pending') {
    throw new Error('No pending booking change request exists.');
  }

  if (decision === 'approved') {
    if (currentRequest.type === 'cancel') {
      await cancelBooking(db, {
        booking: normalizedBooking,
        handledByUserId,
        actorUserId,
        actorName,
      });
    } else if (currentRequest.type === 'reschedule') {
      await updateBooking(db, normalizedBooking.bookingId, {
        startAt: currentRequest.requestedStartAt || normalizedBooking.startAt,
        endAt: currentRequest.requestedEndAt || normalizedBooking.endAt,
        actorUserId,
        actorName,
      });
    }
  }

  const nextChangeRequest = normalizeBookingChangeRequest({
    ...currentRequest,
    status: decision,
    resolutionNote,
    resolvedByUserId: handledByUserId,
    resolvedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  await updateBookingFields(db, normalizedBooking.bookingId, {
    requesterChangeRequest: nextChangeRequest,
  });

  try {
    if (decision === 'approved') {
      await notifyBookingChangeApproved(db, {
        booking: {
          ...normalizedBooking,
          requesterChangeRequest: nextChangeRequest,
        },
        actorUserId,
        actorName,
      });
    } else {
      await notifyBookingChangeDeclined(db, {
        booking: {
          ...normalizedBooking,
          requesterChangeRequest: nextChangeRequest,
        },
        actorUserId,
        actorName,
      });
    }
  } catch (error) {
    console.warn('Booking change request resolution notification failed:', error);
  }

  return {
    bookingId: normalizedBooking.bookingId,
    requesterChangeRequest: nextChangeRequest,
  };
}

export async function sendBookingReminder(db, { booking = {}, actorUserId = '', actorName = '' } = {}) {
  try {
    await notifyUpcomingBookingReminder(db, {
      booking: normalizeBooking(booking),
      actorUserId,
      actorName,
    });
  } catch (error) {
    console.warn('Booking reminder failed:', error);
  }
}

export async function sendPostSessionPrompt(db, { booking = {}, actorUserId = '', actorName = '' } = {}) {
  try {
    await notifyPostSessionUploadPrompt(db, {
      booking: normalizeBooking(booking),
      actorUserId,
      actorName,
    });
  } catch (error) {
    console.warn('Post-session prompt failed:', error);
  }
}

export async function sendFeedbackReadyNotification(db, { booking = {}, actorUserId = '', actorName = '' } = {}) {
  try {
    await notifyFeedbackReadyForBooking(db, {
      booking: normalizeBooking(booking),
      actorUserId,
      actorName,
    });
  } catch (error) {
    console.warn('Feedback-ready booking notification failed:', error);
  }
}

export async function sendRebookPrompt(db, { booking = {}, actorUserId = '', actorName = '' } = {}) {
  try {
    await notifyNextSessionRecommended(db, {
      booking: normalizeBooking(booking),
      actorUserId,
      actorName,
    });
  } catch (error) {
    console.warn('Rebook prompt failed:', error);
  }
}

export async function cancelBooking(db, { booking = {}, handledByUserId = '', actorUserId = '', actorName = '', userData = null, reason = '', suppressNotifications = true, allowSourceOwnedCancel = false } = {}) {
  const normalizedBooking = normalizeBooking(booking);
  if (!normalizedBooking.bookingId) throw new Error('Booking id is required.');
  // Pass 57C (item 8): normalized authorization (owner/admin/ops); production fails closed without actor.
  requireOrgWriteActor({ userData, organizationId: normalizedBooking.organizationId, action: 'cancellation' });
  // Pass 82D §5/§6 — a SOURCE-BACKED commercial booking cannot be cancelled in Motus: cancelling only here would show
  // instructors a cancelled lesson the authoritative source still says is live (a silent divergence). Cancel it in the
  // source schedule, then sync. (`allowSourceOwnedCancel` is a deliberate override for source-driven reconciliation.)
  if (!allowSourceOwnedCancel && isSourceBackedCommercial(normalizedBooking)) {
    const err = new Error(SOURCE_OWNED_EDIT_MESSAGE);
    err.code = 'SOURCE_OWNED';
    throw err;
  }

  await updateDoc(doc(db, PLATFORM_COLLECTIONS.BOOKINGS, normalizedBooking.bookingId), {
    status: 'canceled',
    handledByUserId,
    // audit trail (no hard delete; history retained on the doc itself)
    cancelledByUserId: actorUserId || handledByUserId || '',
    cancelledByName: actorName || '',
    cancellationReason: reason || '',
    cancelledAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  if (normalizedBooking.bookingRequestId) {
    await updateBookingRequestStatus(db, normalizedBooking.bookingRequestId, {
      status: 'canceled',
      handledByUserId,
      handledAt: serverTimestamp(),
      bookingId: normalizedBooking.bookingId,
    });
  }

  // Pass 57C (item 8): outbound notifications remain SUPPRESSED for V1 (default). Cancellation is
  // classified/eligible for later notification (Pass 58) but sends nothing now. The cancellation is
  // still fully audited via the booking doc fields above. When suppressNotifications is false (post-58)
  // the canonical audit + notify path runs.
  if (suppressNotifications) return { bookingId: normalizedBooking.bookingId, status: 'canceled' };

  try {
    await recordAndNotifyStatusChange(db, {
      booking: normalizedBooking,
      changeType: SCHEDULE_CHANGE_TYPES.CANCELED,
      actorUserId,
      actorName,
    });
  } catch (error) {
    console.warn('Schedule cancellation audit/notify failed:', error);
  }

  try {
    await notifyBookingCanceled(db, {
      booking: normalizedBooking,
      actorUserId,
      actorName,
    });
  } catch (error) {
    console.warn('Booking cancellation notification failed:', error);
  }

  return { bookingId: normalizedBooking.bookingId, status: 'canceled' };
}

// Pass 57C (item 1) — the AUTHORITATIVE Publish-Early writer. Reveals ONLY this booking to its assigned
// instructor before the normal publishing window, without changing the org horizon. Fails closed
// (owner/admin/ops); validates the booking belongs to the org, is active, and has an assigned
// instructor. Preserves assignment/client/service/createdAt/policy. Sends NO notification (Pass 58).
export async function publishBookingEarly(db, { bookingId = '', userData = null, actorUserId = '', actorName = '' } = {}) {
  if (!bookingId) throw new Error('Booking id is required.');
  const snap = await getDoc(doc(db, PLATFORM_COLLECTIONS.BOOKINGS, bookingId));
  if (!snap.exists()) throw new Error('Booking not found.');
  const b = normalizeBooking({ id: snap.id, ...snap.data() });
  requireOrgWriteActor({ userData, organizationId: b.organizationId, action: 'publish_early' });
  if (b.status === 'canceled') throw new Error('Cannot publish a cancelled booking.');
  if (!b.instructorId) throw new Error('Booking has no assigned instructor to publish to.');
  await updateDoc(doc(db, PLATFORM_COLLECTIONS.BOOKINGS, bookingId), {
    instructorVisibilityOverride: 'published_early',
    // Pass 58A: also drop the QUERYABLE reveal field to 0 so the deployed instructor query
    // (`where('instructorVisibleFromMs','<=',now)`) and the matching Firestore rule actually expose this
    // one booking now — the override flag alone is not visible to the numeric query/rule model.
    instructorVisibleFromMs: 0,
    publishedToInstructorAt: serverTimestamp(),
    publishedToInstructorBy: actorUserId,
    publishedToInstructorByName: actorName,
    updatedAt: serverTimestamp(),
  });
  return { bookingId, publishedEarly: true };
}

/**
 * Restore a previously-cancelled booking back to the active schedule.
 * Non-destructive counterpart to cancelBooking; audits + notifies the instructor.
 */
export async function restoreBooking(db, { booking = {}, restoreToStatus = 'confirmed', handledByUserId = '', actorUserId = '', actorName = '' } = {}) {
  const normalizedBooking = normalizeBooking(booking);
  if (!normalizedBooking.bookingId) throw new Error('Booking id is required.');

  const nextStatus = ['confirmed', 'pending', 'accepted'].includes(restoreToStatus) ? restoreToStatus : 'confirmed';
  await updateDoc(doc(db, PLATFORM_COLLECTIONS.BOOKINGS, normalizedBooking.bookingId), {
    status: nextStatus,
    handledByUserId,
    updatedAt: serverTimestamp(),
  });

  if (normalizedBooking.bookingRequestId) {
    await updateBookingRequestStatus(db, normalizedBooking.bookingRequestId, {
      status: nextStatus,
      handledByUserId,
      handledAt: serverTimestamp(),
      bookingId: normalizedBooking.bookingId,
    });
  }

  try {
    await recordAndNotifyStatusChange(db, {
      booking: { ...normalizedBooking, status: nextStatus },
      changeType: SCHEDULE_CHANGE_TYPES.RESTORED,
      actorUserId,
      actorName,
    });
  } catch (error) {
    console.warn('Schedule restore audit/notify failed:', error);
  }

  return { bookingId: normalizedBooking.bookingId, status: nextStatus };
}

export async function completeBooking(db, { booking = {}, handledByUserId = '', actorUserId = '', actorName = '' } = {}) {
  const normalizedBooking = normalizeBooking(booking);
  if (!normalizedBooking.bookingId) throw new Error('Booking id is required.');

  await updateDoc(doc(db, PLATFORM_COLLECTIONS.BOOKINGS, normalizedBooking.bookingId), {
    status: 'completed',
    handledByUserId,
    updatedAt: serverTimestamp(),
  });

  if (normalizedBooking.bookingRequestId) {
    await updateBookingRequestStatus(db, normalizedBooking.bookingRequestId, {
      status: 'completed',
      handledByUserId,
      handledAt: serverTimestamp(),
      bookingId: normalizedBooking.bookingId,
    });
  }

  try {
    await notifyBookingCompleted(db, {
      booking: normalizedBooking,
      actorUserId,
      actorName,
    });
  } catch (error) {
    console.warn('Booking completion notification failed:', error);
  }

  return { bookingId: normalizedBooking.bookingId, status: 'completed' };
}

/**
 * Assign (or reassign) an instructor to a booking.
 * Used by the CRM "pull-in" flow when assigning collective/partner
 * instructors to org bookings, or reassigning any booking.
 *
 * Preserves all existing booking fields. Updates instructorId,
 * adds audit trail fields, and sends notifications.
 *
 * Guards:
 *   - rejects cancelled/completed/no_show bookings
 *   - synthesises endAt from duration if missing (for conflict check)
 *   - checks time-range conflicts against the target instructor
 *   - sets assignmentSource for finance/reporting
 */
export async function assignInstructorToBooking(
  db,
  {
    bookingId = '',
    instructorId = '',
    instructorName = '',
    instructorAuthUserId = '', // Pass 48 §25/§26: linked Firebase Auth UID (delivery target)
    assignedByUserId = '',
    assignedByName = '',
    previousInstructorId = '',
    previousInstructorAuthUserId = '',
    assignmentSource = 'org_direct', // 'org_direct' | 'collective_pull_in'
    // Pass 57C (item 11): normalized authorization. When a UI actor context is supplied the write is
    // authorised by role (owner/admin/ops) + org scope; legacy callers/tests without it are unchanged.
    userData = null,
  } = {}
) {
  if (!bookingId) throw new Error('Booking id is required.');
  if (!instructorId) throw new Error('Instructor id is required.');

  const existingSnapshot = await getDoc(doc(db, PLATFORM_COLLECTIONS.BOOKINGS, bookingId));
  if (!existingSnapshot.exists()) throw new Error('Booking not found.');

  const existingBooking = normalizeBooking({ id: existingSnapshot.id, ...existingSnapshot.data() });
  requireOrgWriteActor({ userData, organizationId: existingBooking.organizationId, action: 'assignment' });

  // Status guard — only allow assignment to active bookings
  const blockedStatuses = ['canceled', 'completed', 'no_show'];
  if (blockedStatuses.includes(existingBooking.status)) {
    throw new Error(`Cannot assign instructor to a ${existingBooking.status} booking.`);
  }

  // Prevent no-op reassignment
  if (existingBooking.instructorId === instructorId) {
    throw new Error('This instructor is already assigned to this booking.');
  }

  // Conflict check — synthesise endAt from duration if missing
  if (existingBooking.startAt) {
    let effectiveEndAt = existingBooking.endAt;
    if (!effectiveEndAt && existingBooking.startAt) {
      const startDate = toDateValue(existingBooking.startAt);
      if (startDate) {
        const durationMs = (existingBooking.serviceDurationSnapshot || 60) * 60 * 1000;
        effectiveEndAt = new Date(startDate.getTime() + durationMs);
      }
    }

    const testBooking = { ...existingBooking, instructorId, endAt: effectiveEndAt || existingBooking.endAt };
    if (await hasBookingConflict(db, testBooking, { excludeBookingId: bookingId })) {
      throw new Error('This instructor has a conflicting booking at the same time.');
    }
  }

  const isReassignment = !!(previousInstructorId || existingBooking.instructorId);
  const resolvedPreviousId = previousInstructorId || existingBooking.instructorId || null;

  // Pass 49A §review2: the delivery target is DERIVED from the canonical organizationMember
  // (instructorId + org), never taken from the caller. A caller-supplied instructorAuthUserId
  // is accepted ONLY if it exactly matches the resolved member UID; otherwise it is ignored
  // and can never override canonical membership (and never attach another user's Auth UID).
  const canonicalAuthUid = instructorId
    ? await resolveMemberAuthUserId(db, existingBooking.organizationId, instructorId)
    : '';
  if (instructorAuthUserId && instructorAuthUserId !== canonicalAuthUid) {
    console.warn('Ignoring caller-supplied instructorAuthUserId that does not match the canonical member', { bookingId, instructorId });
  }
  const resolvedAuthUid = canonicalAuthUid;

  const updates = {
    instructorId,                                   // canonical member id (unchanged model)
    // Pass 48 §26: additive linked Auth UID → notification/read delivery. Null when the
    // member has no Auth account (assignment still succeeds operationally; delivery n/a).
    instructorAuthUserId: resolvedAuthUid || null,
    // Pass 49A §1: a booking with an instructor is no longer "unassigned".
    assignmentState: 'assigned',
    assignmentSource,
    updatedAt: serverTimestamp(),
  };

  if (instructorName) {
    updates.instructorNameSnapshot = instructorName;
  }

  // Pass 49A §1/§review3: TARGETED resolution of the instructor-assignment review only.
  // Remove just the "instructor assignment" reason (preserving unrelated reasons such as
  // "identity conflict"), then RECOMPUTE readiness canonically. If other fields are still
  // missing, needsReviewReason reflects those real remaining issues — it never keeps the
  // stale "instructor assignment" phrase, and never erases unrelated review reasons.
  if (/instructor assignment/i.test(String(existingBooking.needsReviewReason || ''))) {
    const remaining = removeReviewReasonToken(existingBooking.needsReviewReason, 'instructor assignment');
    const readiness = assessOperationalReadiness({ ...existingBooking, instructorId });
    const missingReason = readiness.ready ? '' : readiness.missing.join(', ');
    updates.needsReviewReason = [remaining, missingReason].filter(Boolean).join('; ');
    updates.needsReview = !!updates.needsReviewReason;
    updates.readiness = readiness.ready ? '' : `NEEDS ATTENTION: ${readiness.missing.join(', ')}`;
  }

  // Audit trail for reassignment (retain previous canonical id AND previous auth target)
  if (isReassignment) {
    updates.previousInstructorId = resolvedPreviousId;
    updates.previousInstructorAuthUserId = previousInstructorAuthUserId || existingBooking.instructorAuthUserId || null;
    updates.reassignedAt = serverTimestamp();
    updates.reassignedByUserId = assignedByUserId;
  }

  await updateDoc(doc(db, PLATFORM_COLLECTIONS.BOOKINGS, bookingId), updates);

  const updatedBooking = { ...existingBooking, ...updates, instructorId };

  // Instructor notifications + audit history (new instructor gains the lesson,
  // previous instructor is told it was removed). This is the single source of
  // truth for instructor-side assignment notices.
  try {
    await recordAndNotifyReassignment(db, {
      booking: updatedBooking,
      previousInstructorId: isReassignment ? resolvedPreviousId : '',
      newInstructorId: instructorId,
      newInstructorAuthUserId: resolvedAuthUid || null,             // Pass 49A §6 resolved delivery target
      previousInstructorAuthUserId: previousInstructorAuthUserId || existingBooking.instructorAuthUserId || null,
      actorUserId: assignedByUserId,
      actorName: assignedByName,
    });
  } catch (err) {
    console.warn('Assignment audit/notify failed:', err);
  }

  // Client-facing notification (instructor changed / assigned) — client only.
  try {
    await _notifyBookingAssignment(db, {
      booking: updatedBooking,
      isReassignment,
      previousInstructorId: resolvedPreviousId,
      actorUserId: assignedByUserId,
      actorName: assignedByName,
    });
  } catch (err) {
    console.warn('Booking assignment client notification failed:', err);
  }

  return { bookingId, instructorId, status: 'assigned', isReassignment };
}

/**
 * Move a booking back to the unassigned queue (removes the instructor).
 * Audits + notifies the instructor who lost the lesson.
 */
export async function unassignBooking(db, { bookingId = '', actorUserId = '', actorName = '' } = {}) {
  if (!bookingId) throw new Error('Booking id is required.');
  const existingSnapshot = await getDoc(doc(db, PLATFORM_COLLECTIONS.BOOKINGS, bookingId));
  if (!existingSnapshot.exists()) throw new Error('Booking not found.');
  const existingBooking = normalizeBooking({ id: existingSnapshot.id, ...existingSnapshot.data() });
  const previousInstructorId = existingBooking.instructorId || '';
  // Pass 49A §3/§4: capture BOTH old identities; the Auth link must be cleared so the
  // previous instructor no longer matches instructorAuthUserId==uid (read rule + query).
  const previousInstructorAuthUserId = existingBooking.instructorAuthUserId || null;
  // §4: if NEITHER assignment identity exists there is genuinely nothing to clear.
  // But a stale Auth-only state (instructorId='' yet instructorAuthUserId set) MUST be
  // normalised — so we still proceed when previousInstructorAuthUserId is present.
  if (!previousInstructorId && !previousInstructorAuthUserId) {
    return { bookingId, instructorId: '', instructorAuthUserId: null, status: 'unassigned', isReassignment: false };
  }

  await updateDoc(doc(db, PLATFORM_COLLECTIONS.BOOKINGS, bookingId), {
    instructorId: '',
    instructorAuthUserId: null,                 // §3/§4: revoke old Auth read/query match
    previousInstructorId,
    previousInstructorAuthUserId,
    assignmentState: 'unassigned',
    assignmentSource: 'unassigned',
    reassignedAt: serverTimestamp(),
    reassignedByUserId: actorUserId,
    updatedAt: serverTimestamp(),
  });

  // Notify only when there was a real previous canonical instructor to inform.
  if (previousInstructorId) {
    try {
      await recordAndNotifyReassignment(db, {
        booking: { ...existingBooking, instructorId: '', instructorAuthUserId: null },
        previousInstructorId,
        previousInstructorAuthUserId,            // old Auth target gets the "removed" notice
        newInstructorId: '',
        actorUserId,
        actorName,
      });
    } catch (err) {
      console.warn('Unassign audit/notify failed:', err);
    }
  }

  return { bookingId, instructorId: '', instructorAuthUserId: null, previousInstructorId, previousInstructorAuthUserId, status: 'unassigned', isReassignment: !!previousInstructorId };
}

/**
 * Internal: notify the CLIENT/requester that their instructor has changed.
 * (Instructor-side notifications + audit are owned by recordAndNotifyReassignment
 * so the new instructor is not notified twice.)
 */
async function _notifyBookingAssignment(db, { booking = {}, isReassignment = false, previousInstructorId = '', actorUserId = '', actorName = '' } = {}) {
  const { createNotification } = await import('./notifications');

  // Notify client only
  const requesterUserId = booking.requesterUserId || '';
  if (requesterUserId) {
    await createNotification(db, {
      userId: requesterUserId,
      organizationId: booking.organizationId,
      audience: 'commercial', // Pass 85.1B.3 — booking requester (commercial), fail-closed for the staff branch
      type: isReassignment ? 'booking_instructor_changed' : 'booking_instructor_assigned',
      title: isReassignment ? 'Instructor updated' : 'Instructor assigned',
      message: booking.instructorNameSnapshot
        ? `${booking.instructorNameSnapshot} has been assigned to your booking.`
        : 'An instructor has been assigned to your booking.',
      link: '/my-bookings',
      actorUserId,
      actorName,
      relatedEntityType: 'booking',
      relatedEntityId: booking.bookingId || booking.id,
    });
  }
}
